/**
 * A neutral shade, for lists that need their items told apart but not ranked
 * by colour (CLAUDE.md invariant 4: monochrome plus green and red, nothing
 * else).
 *
 * Two lists were using the full Material palette — sector breakdowns in blue,
 * orange, purple, amber and cyan, and user avatars in sixteen hues (2026-09-07
 * quality report). Neither hue carried meaning: the sector's own name sits
 * beside its dot and its bar length is the weight, and an avatar's colour says
 * nothing about the person. A hue that means nothing still reads as if it
 * does, which is why the palette rule exists.
 *
 * Two ways in, because the two lists differ in what "next to each other" means:
 *
 *   * `rampByRank` — for an ORDERED list. The first item is darkest and each
 *     one after it lighter, so the shade repeats the ranking the order already
 *     shows instead of contradicting it.
 *   * `rampByName` — for an UNORDERED set (avatar icons). Deterministic on the
 *     name, so one person's avatar keeps its shade between sessions.
 */
import type { Theme } from '@mui/material';

/** Dark to light. Stops short of the palest greys, which vanish on a card. */
const STEPS = [900, 800, 700, 600, 500, 400] as const;

export function rampByRank(theme: Theme, index: number): string {
  const step = STEPS[Math.min(Math.max(index, 0), STEPS.length - 1)];
  return theme.palette.grey[step];
}

export function rampByName(theme: Theme, name: string): string {
  // Sum of code points: stable across runs and machines, which `Math.random`
  // or an insertion order would not be — an avatar that changes shade on a
  // reload reads as a bug.
  let total = 0;
  for (let i = 0; i < name.length; i += 1) total += name.charCodeAt(i);
  return theme.palette.grey[STEPS[total % STEPS.length]];
}

export const RAMP_STEPS = STEPS;
